import { useState, useEffect, useRef,memo } from "react"
import io from 'socket.io-client';
import Peer from 'simple-peer';
import { useMemo } from "react";
import { LanguageSelect, useLocale } from '../../locale';
import { androidEntry, roomJoinPayload, peerDisplayName } from './androidEntry';
import './AndroidWindows.css';
// 스타일 임포트
import {
  ModalContainer,
  FlexColumn,
  Header,
  HeaderLeft,
  HeaderRight,
  Title,
  ParticipantCount,
  ParticipantCountText,
  MainContent,
  VideoGrid,
  Controls,
  Button,
  VideoContainer,
  VideoPlaceholder,
  VideoFrame,
  VideoImage,
  AudioLevel,
  VideoInfo,
  VideoInfoContent,
  VideoInfoLeft,
  VideoInfoRight,
  VideoName,
  Avatar,
  AvatarImage,
  Icon,
  ToolboxContainer,
  ToolboxButton,
  ParticipantDrawer,
  ParticipantItem,
  ParticipantInfo,
  ParticipantActions,
  DrawerHeader,
  MediaSelectorOverlay,
  MediaSelectorCard,
  MediaSelectorTitle,
  MediaOptionContainer,
  MediaOptionButton,
  IconContainer,
  OptionLabel,
  OptionDescription,
  colors
} from "./FaceTalkStyle";

// 아이콘 임포트
import {
  XIcon,
  MicIcon,
  MicOffIcon,
  VideoIcon,
  VideoOffIcon,
  Share2Icon,
  PhoneOffIcon,
  UsersIcon,
  Volume2Icon,
  SettingsIcon,
  EmojiIcon,
  HandIcon,
  ChatIcon,
  UserPlusIcon
} from "./FaceTalkStyle";
// 추가 import


// 시간 표시를 위한 분리된 메모이제이션 컴포넌트
const MeetingTimer = memo(({ seconds }) => {
  const formattedTime = useMemo(() => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, [seconds]);
  
  return <span>{formattedTime}</span>;
});

// 연결 상태 컴포넌트
const ConnectionStatus = memo(({ status }) => {
  const { t } = useLocale();
  switch(status) {
    case 'connected':
      return <span style={{color: colors.success}}>{t('연결됨')}</span>;
    case 'disconnected':
      return <span style={{color: colors.danger}}>{t('연결 끊김')}</span>;
    case 'error':
      return <span style={{color: 'orange'}}>{t('연결 오류')}</span>;
    default:
      return null;
  }
});

const VideoConferenceModal = ({ isOpen, onClose, roomId }) => {
  const { t } = useLocale();
  // Deferred permission failures use the current language without replaying the request or alert.
  const translateRef = useRef(t);
  translateRef.current = t;
  const entry = androidEntry();
  // 상태 관리
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRaiseHand, setIsRaiseHand] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [meetingTime, setMeetingTime] = useState(0);
  const [actualParticipantCount, setActualParticipantCount] = useState(0);
  const [displayedParticipantCount, setDisplayedParticipantCount] = useState(androidEntry() ? 1 : 5);
  const [isParticipantDrawerOpen, setIsParticipantDrawerOpen] = useState(false);
  const [isToolboxOpen, setIsToolboxOpen] = useState(false);
  const [peers, setPeers] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [mediaType, setMediaType] = useState(null); // 'camera' 또는 'screen'
  const [showMediaSelector, setShowMediaSelector] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const meetingTimeRef = useRef(0);
  // 참조 객체
  const socketRef = useRef();
  const userVideoRef = useRef();
  const peersRef = useRef([]);
  const localStreamRef = useRef();
  const screenStreamRef = useRef(null);
  const canShareScreen = typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  // 끊겼다 다시 붙었을 때 어느 방으로 돌아갈지 기억한다.
  const joinedRoomRef = useRef(null);

  // 미디어 타입 선택 함수
  const selectMediaType = (type) => {
    if (type === 'screen' && !canShareScreen) return;
    setMediaType(type);
    setShowMediaSelector(false);
    
    if (type === 'audio') {
      setIsVideoOff(true);
      navigator.mediaDevices.getUserMedia({ video: false, audio: true })
        .then(handleStreamSuccess)
        .catch(handleStreamError);
    } else if (type === 'camera') {
      // 카메라 스트림 가져오기
      navigator.mediaDevices.getUserMedia({ video: !isVideoOff, audio: !isMuted })
        .then(handleStreamSuccess)
        .catch(handleStreamError);
    } else if (type === 'screen') {
      // 화면 공유 스트림 가져오기
      navigator.mediaDevices.getDisplayMedia({ 
        video: { cursor: "always" },
        audio: true 
      })
        .then(handleStreamSuccess)
        .catch(error => {
          console.error("화면 공유 오류:", error);
          alert(translateRef.current('화면 공유를 시작할 수 없습니다: {detail}').replace('{detail}', () => error.message));
          // 오류 시 카메라로 폴백
          setMediaType('camera');
          navigator.mediaDevices.getUserMedia({ video: !isVideoOff, audio: !isMuted })
            .then(handleStreamSuccess)
            .catch(handleStreamError);
        });
    }
  };

  // 스트림 성공 처리 함수
  const handleStreamSuccess = (stream) => {
    console.log(`${mediaType === 'camera' ? '카메라' : '화면 공유'} 스트림 획득 성공`);
    
    userVideoRef.current.srcObject = stream;
    localStreamRef.current = stream;
    
    // 화면 공유인 경우 종료 이벤트 리스너 추가
    if (mediaType === 'screen') {
      setIsScreenSharing(true);
      // 중지 버튼이 캡처를 실제로 닫을 수 있게 스트림을 보관한다.
      screenStreamRef.current = stream;
      const screenTrack = stream.getVideoTracks()[0];

      screenTrack.onended = () => {
        console.log('사용자가 화면 공유를 중지함');
        setIsScreenSharing(false);
        // 화면 공유 종료 시 카메라로 전환
        setMediaType('camera');
        navigator.mediaDevices.getUserMedia({ video: !isVideoOff, audio: !isMuted })
          .then(handleStreamSuccess)
          .catch(handleStreamError);
      };
    }
    
    // 리스너를 먼저 걸어야 입장 직후 서버가 보내는 참가자 목록을 놓치지 않는다.
    setupSocketListeners(stream);

    // TURN 자격증명을 받은 뒤에 입장한다. 먼저 입장하면 피어가 릴레이 없이 만들어진다.
    const roomToJoin = roomId || `default-room-${Date.now()}`;
    loadIceServers().then(() => {
      if (!socketRef.current) return;
      console.log(`방 참여 시도: ${roomToJoin}`);
      joinedRoomRef.current = roomToJoin;
      socketRef.current.emit('join room', roomJoinPayload(roomToJoin));
    });
  };

  // 스트림 오류 처리 함수
  const handleStreamError = (err) => {
    console.error("미디어 장치 접근 오류:", err);
    
    // 오류 유형에 따라 다른 처리
    if (err.name === 'NotFoundError') {
      alert(translateRef.current('카메라 또는 마이크를 찾을 수 없습니다. 장치가 연결되어 있는지 확인해주세요.'));
      
      // 비디오 없이 오디오만으로 시도
      return navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then(handleStreamSuccess)
        .catch(error => {
          console.error("오디오만 사용 시도 중 오류:", error);
          // 빈 미디어 스트림으로 참여
          handleEmptyStream();
        });
    } else if (err.name === 'NotAllowedError') {
      alert(translateRef.current('카메라와 마이크 접근 권한이 필요합니다. 브라우저 권한을 확인해주세요.'));
    } else {
      alert(translateRef.current('미디어 장치 접근 오류: {detail}').replace('{detail}', () => err.message));
    }
    
    // 빈 미디어 스트림으로 참여
    handleEmptyStream();
  };

  // 빈 스트림으로 참여하는 함수
  const handleEmptyStream = () => {
    console.log('미디어 장치 없이 참여합니다');
    setIsVideoOff(true);
    setIsMuted(true);
    
    // 빈 스트림 생성
    const emptyStream = new MediaStream();
    userVideoRef.current.srcObject = emptyStream;
    localStreamRef.current = emptyStream;
    
    // 리스너를 먼저 걸고, TURN 자격증명을 받은 뒤에 입장한다.
    setupSocketListeners(emptyStream);

    const roomToJoin = roomId || `default-room-${Date.now()}`;
    loadIceServers().then(() => {
      if (!socketRef.current) return;
      joinedRoomRef.current = roomToJoin;
      socketRef.current.emit('join room', roomJoinPayload(roomToJoin));
    });
  };

  // 소켓 이벤트 리스너 설정 함수
  const setupSocketListeners = (stream) => {
    // 참가자 수 업데이트
    socketRef.current.on('user count', count => {
      console.log(`참가자 수 업데이트: ${count}`);
      setActualParticipantCount(count);
      // 실제 참가자 수가 표시 수보다 작으면 표시 수도 업데이트
      if (entry || count < displayedParticipantCount) {
        setDisplayedParticipantCount(count);
      }
    });

    // 기존 사용자들에게 연결
    socketRef.current.on('all users', users => {
      console.log('기존 사용자 목록 수신:', users);

      // 서버는 이름을 함께 주려고 { id, name } 을 보낸다. 예전 문자열 형식도 받는다.
      // 객체를 그대로 상대 식별자로 쓰면 신호가 아무에게도 전달되지 않고,
      // 화면을 그릴 때 식별자에 문자열 함수를 불러 회의 창이 검은 채로 멈춘다.
      const normalized = (users || [])
        .map(user => typeof user === 'string' ? { id: user, name: '' } : { id: user?.id, name: peerDisplayName(user?.name) })
        .filter(user => typeof user.id === 'string' && user.id.length > 0);

      // 재접속으로 목록을 다시 받으면 이전 피어가 남아 연결이 중복된다.
      peersRef.current.forEach(({ peer }) => {
        if (peer && !peer.destroyed) peer.destroy();
      });
      Object.values(iceRecoveryRef.current).forEach(clearTimeout);
      iceRecoveryRef.current = {};

      const joined = normalized.map(({ id: peerID, name }) => {
        console.log(`기존 사용자에게 연결 시도: ${peerID}`);
        return { peerID, name, peer: createPeer(peerID, socketRef.current.id, stream) };
      });

      peersRef.current = joined;
      setPeers(joined);
    });

    // 새 사용자 연결
    socketRef.current.on('user joined', payload => {
      // 같은 상대에게서 오는 두 번째 이후 신호는 ICE 후보나 재협상·ICE 재시작 제안이다.
      // 이때 피어를 새로 만들면 연결이 중복으로 쌓이고 복구가 영구히 실패한다.
      const known = peersRef.current.find(p => p.peerID === payload.callerID);
      if (known) {
        console.log(`기존 피어에 추가 신호 전달: ${payload.callerID}`);
        try {
          known.peer.signal(payload.signal);
        } catch (error) {
          console.error(`추가 신호 처리 실패 (${payload.callerID}):`, error);
        }
        return;
      }

      console.log(`새 사용자가 참여함: ${payload.callerID}`);
      const peer = addPeer(payload.signal, payload.callerID, stream);
      // The initial roster is also React state. Mutating it then appending in
      // setPeers counted one newly joined peer twice (two people displayed as three).
      peersRef.current = [...peersRef.current, {
        peerID: payload.callerID,
        name: peerDisplayName(payload.callerName),
        peer,
      }];
      setPeers([...peersRef.current]);
    });

    // 응답 신호 받기
    socketRef.current.on('receiving returned signal', payload => {
      console.log(`응답 신호 수신: ${payload.id}`);
      const item = peersRef.current.find(p => p.peerID === payload.id);
      if (item) {
        item.peer.signal(payload.signal);
      } else {
        console.warn(`해당 피어를 찾을 수 없음: ${payload.id}`);
      }
    });
    
    // 사용자 퇴장 이벤트
    socketRef.current.on('user left', userId => {
      console.log(`사용자 퇴장: ${userId}`);

      // 목록에서만 지우면 RTCPeerConnection 과 복구 타이머가 그대로 남아
      // 회의가 길어질수록 연결이 쌓이고 끊김이 잦아진다.
      const leaving = peersRef.current.find(p => p.peerID === userId);
      if (leaving && leaving.peer && !leaving.peer.destroyed) {
        leaving.peer.destroy();
      }
      clearTimeout(iceRecoveryRef.current[userId]);
      delete iceRecoveryRef.current[userId];

      peersRef.current = peersRef.current.filter(p => p.peerID !== userId);
      setPeers(peers => peers.filter(p => p.peerID !== userId));
    });

    // 손들기 이벤트 수신
    socketRef.current.on('user raised hand', payload => {
      console.log(`사용자 ${payload.userId}가 손을 ${payload.raised ? '들었습니다' : '내렸습니다'}`);
      // 여기에 UI 업데이트 로직 추가 가능
    });
  };

  // 모바일 감지
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 연결 설정 및 정리
  useEffect(() => {
    if (!isOpen) return;

    console.log('화상 회의 컴포넌트 마운트. 룸 ID:', roomId || 'default-room');

    // 소켓 연결 - 서버에 맞게 URL 수정
    socketRef.current = io.connect(window.location.origin, {
      reconnection: true,
      // 5번만 시도하면 잠깐 끊긴 뒤 영구히 포기해 회의가 되살아나지 못한다.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    // 연결 상태 이벤트 리스너
    socketRef.current.on('connect', () => {
      console.log('소켓 서버에 연결됨. 소켓 ID:', socketRef.current.id);
      setConnectionStatus('connected');

      // 다시 붙은 소켓은 서버가 보기엔 새 사용자다. 방에 다시 들어가지 않으면
      // 참가자 목록에서 빠진 채로 남아 화면이 검은 상태로 멈춘다.
      if (joinedRoomRef.current) {
        console.log(`재접속 후 방 복귀: ${joinedRoomRef.current}`);
        socketRef.current.emit('join room', roomJoinPayload(joinedRoomRef.current));
      }
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('소켓 연결 오류:', error);
      setConnectionStatus('error');
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('소켓 연결 끊김:', reason);
      setConnectionStatus('disconnected');
    });
    
    // 미디어 접근은 사용자 선택 후에 수행됨
    
    // 타이머 설정
    const timer = setInterval(() => {
      meetingTimeRef.current += 1;
      // 1초마다 상태 업데이트하는 대신 3초마다 업데이트하여 리렌더링 횟수 줄임
      if (meetingTimeRef.current % 3 === 0) {
        setMeetingTime(meetingTimeRef.current);
      }
    }, 1000);

    // 컴포넌트 언마운트 시 정리
    return () => {
      console.log('화상 회의 컴포넌트 언마운트. 연결 정리 중...');
      
      // 소켓 연결 해제
      if (socketRef.current) {
        // 실제로 들어간 방 이름으로 나가야 한다. 새로 지어낸 이름을 보내면
        // 서버 참가자 목록에 내가 남아 다른 사람 화면에 멈춘 칸이 남는다.
        const roomToLeave = joinedRoomRef.current || roomId;
        if (roomToLeave) socketRef.current.emit('leave room', roomToLeave);
        socketRef.current.disconnect();
        console.log('소켓 연결 해제됨');
      }
      joinedRoomRef.current = null;
      
      // 비디오 트랙 중지
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log(`트랙 중지됨: ${track.kind}`);
        });
      }
      
      // 화면 공유 캡처 종료
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }

      // 피어 연결 해제
      peersRef.current.forEach(peer => {
        if (peer.peer) {
          peer.peer.destroy();
          console.log(`피어 연결 해제: ${peer.peerID}`);
        }
      });
      peersRef.current = [];

      // ICE 복구 대기 타이머 정리
      Object.values(iceRecoveryRef.current).forEach(clearTimeout);
      iceRecoveryRef.current = {};

      // 타이머 정리
      clearInterval(timer);
    };
  }, [isOpen, roomId]);

  // ICE 서버 설정 (STUN/TURN)
  // STUN만으로는 대칭 NAT·사내망에서 쓸 수 있는 후보가 하나도 안 생겨
  // 연결이 붙지 않거나 붙었다가 곧 끊긴다. 릴레이 후보는 loadIceServers()가 채운다.
  const iceConfigRef = useRef({
    iceServers: [
      { urls: 'stun:cast.antsnest.co.kr:3478' },
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
    iceCandidatePoolSize: 2,
  });
  const icePromiseRef = useRef(null);
  const iceRecoveryRef = useRef({});

  // coturn의 단기 자격증명을 받아 TURN 릴레이까지 쓸 수 있게 만든다.
  // 실패해도 STUN만으로 계속 진행한다 — 회의 자체를 막지는 않는다.
  const loadIceServers = () => {
    if (icePromiseRef.current) return icePromiseRef.current;

    icePromiseRef.current = fetch('/turn-credentials', { cache: 'no-store' })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(({ username, credential }) => {
        if (!username || !credential) throw new Error('자격증명이 비어 있음');
        iceConfigRef.current = {
          iceServers: [
            { urls: 'stun:cast.antsnest.co.kr:3478' },
            { urls: 'stun:stun.l.google.com:19302' },
            {
              urls: [
                'turn:cast.antsnest.co.kr:3478?transport=udp',
                'turn:cast.antsnest.co.kr:3478?transport=tcp',
              ],
              username,
              credential,
            },
          ],
          iceCandidatePoolSize: 2,
        };
        console.log('TURN 자격증명 적용됨');
      })
      .catch(err => {
        console.warn(`TURN 자격증명을 받지 못해 STUN만 사용합니다: ${err.message}`);
      });

    return icePromiseRef.current;
  };

  // ICE가 끊어지면 simple-peer는 오류도 없이 조용히 멈춘다.
  // 그래서 화면이 검은 채로 남는다. 상태를 직접 보고 살려낸다.
  const watchIceState = (peer, peerID, isInitiator) => {
    const pc = peer._pc;
    if (!pc) return;

    pc.addEventListener('iceconnectionstatechange', () => {
      const state = pc.iceConnectionState;
      console.log(`ICE 상태 변경 (${peerID}): ${state}`);

      if (state === 'connected' || state === 'completed') {
        clearTimeout(iceRecoveryRef.current[peerID]);
        delete iceRecoveryRef.current[peerID];
        return;
      }

      if (state !== 'disconnected' && state !== 'failed') return;

      // disconnected 는 잠깐 흔들린 것일 수 있으니 바로 재시작하지 않는다.
      const delay = state === 'failed' ? 0 : 3000;
      clearTimeout(iceRecoveryRef.current[peerID]);
      iceRecoveryRef.current[peerID] = setTimeout(() => {
        if (peer.destroyed) return;
        const current = pc.iceConnectionState;
        if (current === 'connected' || current === 'completed' || current === 'closed') return;

        // 재시작은 제안을 만드는 쪽(initiator)만 할 수 있다.
        // 반대쪽은 상대가 보낸 새 제안을 받아 자동으로 함께 복구된다.
        if (!isInitiator || typeof pc.restartIce !== 'function') {
          console.warn(`ICE 복구를 상대 쪽에 맡김 (${peerID}): ${current}`);
          return;
        }

        try {
          console.log(`ICE 재시작 시도 (${peerID})`);
          pc.restartIce();
        } catch (error) {
          console.error(`ICE 재시작 실패 (${peerID}):`, error);
        }
      }, delay);
    });
  };

  // 비디오 송신자가 있으면 트랙을 교체하고, 없으면 새로 올려 재협상한다.
  // 송신자가 없을 때 그냥 경고만 남기면 상대 화면이 영구히 검게 남는다.
  const publishVideoTrack = (track) => {
    peersRef.current.forEach(({ peerID, peer }) => {
      if (!peer || peer.destroyed) return;
      try {
        const pc = peer._pc;
        const sender = pc && pc.getSenders
          ? pc.getSenders().find(s => s.track && s.track.kind === 'video')
          : null;

        if (sender) {
          sender.replaceTrack(track);
          console.log(`비디오 트랙 교체 성공 (${peerID})`);
        } else if (track && localStreamRef.current) {
          peer.addTrack(track, localStreamRef.current);
          console.log(`비디오 트랙 신규 전송 (${peerID})`);
        }
      } catch (error) {
        console.error(`비디오 트랙 전송 실패 (${peerID}):`, error);
      }
    });
  };

  // 피어 생성 함수
  function createPeer(userToSignal, callerID, stream) {
    console.log(`피어 생성: ${userToSignal}`);
    const peer = new Peer({
      initiator: true,
      // 후보를 모두 모을 때까지 기다리면 STUN이 막힌 망에서 연결이 한참 지연되거나
      // 끝내 실패한다. 생기는 대로 보내면 쓸 수 있는 경로부터 먼저 붙는다.
      trickle: true,
      stream,
      config: iceConfigRef.current,
    });

    peer.on('signal', signal => {
      console.log(`신호 생성됨 (initiator): ${userToSignal}`);
      socketRef.current.emit('sending signal', { userToSignal, callerID, signal });
    });

    watchIceState(peer, userToSignal, true);

    peer.on('stream', remoteStream => {
      console.log(`원격 스트림 수신 (initiator): ${userToSignal}`);
      setPeers(prev => prev.map(p =>
        p.peerID === userToSignal ? { ...p, remoteStream } : p
      ));
    });

    peer.on('error', err => {
      console.error(`피어 오류 (initiator): ${err.message}`);
    });

    return peer;
  }

  // 피어 추가 함수
  function addPeer(incomingSignal, callerID, stream) {
    console.log(`피어 추가: ${callerID}`);
    const peer = new Peer({
      initiator: false,
      trickle: true,
      stream,
      config: iceConfigRef.current,
    });

    peer.on('signal', signal => {
      console.log(`신호 생성됨 (receiver): ${callerID}`);
      socketRef.current.emit('returning signal', { signal, callerID });
    });

    watchIceState(peer, callerID, false);

    peer.on('stream', remoteStream => {
      console.log(`원격 스트림 수신 (receiver): ${callerID}`);
      setPeers(prev => prev.map(p =>
        p.peerID === callerID ? { ...p, remoteStream } : p
      ));
    });

    peer.on('error', err => {
      console.error(`피어 오류 (receiver): ${err.message}`);
    });

    peer.signal(incomingSignal);

    return peer;
  }

  // 오디오 토글
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = isMuted;
        console.log(`오디오 트랙 상태 변경: ${track.enabled ? '켜짐' : '꺼짐'}`);
      });
      setIsMuted(!isMuted);
    }
  };

  // 비디오 토글
  const toggleVideo = () => {
    if (!localStreamRef.current) return;

    const videoTracks = localStreamRef.current.getVideoTracks();

    // 카메라 없이 입장했으면 켤 트랙 자체가 없다. 이때는 장치를 새로 얻어
    // 상대에게 올려야 한다. 상태만 바꾸면 상대 화면은 계속 검게 남는다.
    if (videoTracks.length === 0) {
      if (!isVideoOff) return;

      navigator.mediaDevices.getUserMedia({ video: true, audio: false })
        .then(stream => {
          const track = stream.getVideoTracks()[0];
          if (!track) return;

          localStreamRef.current.addTrack(track);
          if (userVideoRef.current) {
            userVideoRef.current.srcObject = localStreamRef.current;
          }
          publishVideoTrack(track);
          setIsVideoOff(false);
        })
        .catch(err => {
          console.error('카메라를 켤 수 없음:', err);
          alert(translateRef.current('카메라를 켤 수 없습니다: {detail}').replace('{detail}', () => err.message));
        });
      return;
    }

    videoTracks.forEach(track => {
      track.enabled = isVideoOff;
      console.log(`비디오 트랙 상태 변경: ${track.enabled ? '켜짐' : '꺼짐'}`);
    });
    setIsVideoOff(!isVideoOff);
  };

  // 공유를 멈출 때 캡처 트랙까지 반드시 닫는다. 멈추지 않으면 공유를 끈 뒤에도
  // 브라우저가 화면을 계속 읽어 상대에게 멈춘 화면이 남는다.
  const stopScreenStream = () => {
    if (!screenStreamRef.current) return;
    screenStreamRef.current.getTracks().forEach(track => track.stop());
    screenStreamRef.current = null;
  };

  // 원래 카메라 트랙으로 되돌린다.
  // 화면 공유로 입장했으면 되돌릴 카메라 트랙이 없으므로 그때 장치를 얻는다.
  const restoreCameraTrack = () => {
    const stream = localStreamRef.current;
    const liveCamera = stream
      ? stream.getVideoTracks().find(track => track.readyState === 'live')
      : null;

    if (liveCamera) {
      publishVideoTrack(liveCamera);
      if (userVideoRef.current) userVideoRef.current.srcObject = stream;
      return;
    }

    // 끝난 트랙은 더 보낼 수 없으니 먼저 전송을 비우고 치운다.
    publishVideoTrack(null);
    if (stream) {
      stream.getVideoTracks().forEach(track => stream.removeTrack(track));
    }

    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then(cameraStream => {
        const track = cameraStream.getVideoTracks()[0];
        if (!track || !localStreamRef.current) return;

        localStreamRef.current.addTrack(track);
        if (userVideoRef.current) {
          userVideoRef.current.srcObject = localStreamRef.current;
        }
        publishVideoTrack(track);
        setIsVideoOff(false);
      })
      .catch(err => {
        console.warn(`공유 종료 후 카메라로 돌아가지 못했습니다: ${err.message}`);
        setIsVideoOff(true);
        if (userVideoRef.current && localStreamRef.current) {
          userVideoRef.current.srcObject = localStreamRef.current;
        }
      });
  };

  // 화면 공유 토글
  const toggleScreenSharing = () => {
    if (!canShareScreen) return;
    if (!isScreenSharing) {
      console.log('화면 공유 시작');
      navigator.mediaDevices.getDisplayMedia({ 
        video: { cursor: "always" },
        audio: true 
      })
        .then(stream => {
          const screenTrack = stream.getVideoTracks()[0];
          console.log('화면 공유 스트림 획득 성공');
          
          // 공유를 끈 뒤에도 브라우저가 화면을 계속 캡처하지 않도록 스트림을 들고 있는다.
          screenStreamRef.current = stream;

          // 기존 피어 연결에 화면 공유 트랙 전달
          publishVideoTrack(screenTrack);

          // 브라우저 자체의 '공유 중지' 버튼으로 끝낸 경우
          screenTrack.onended = () => {
            console.log('사용자가 화면 공유를 중지함');
            stopScreenStream();
            restoreCameraTrack();
            setIsScreenSharing(false);
          };

          // 로컬 비디오에 화면 공유 표시
          const newStream = new MediaStream([screenTrack, ...localStreamRef.current.getAudioTracks()]);
          userVideoRef.current.srcObject = newStream;
          setIsScreenSharing(true);
        })
        .catch(err => {
          console.error("화면 공유 오류:", err);
          alert(translateRef.current('화면 공유를 시작할 수 없습니다: {detail}').replace('{detail}', () => err.message));
        });
    } else {
      console.log('화면 공유 중지');
      stopScreenStream();
      restoreCameraTrack();
      setIsScreenSharing(false);
    }
  };

  // 참가자 수 증가/감소 함수 수정
  const decreaseParticipants = () => {
    if (displayedParticipantCount > 2) {
      setDisplayedParticipantCount(displayedParticipantCount - 1);
      console.log(`표시 참가자 수 감소: ${displayedParticipantCount - 1}`);
    }
  };

  const increaseParticipants = () => {
    console.log("현재:", displayedParticipantCount);
    if (displayedParticipantCount < 8) {
      const newCount = displayedParticipantCount + 1;
      console.log("변경 후:", newCount);
      setDisplayedParticipantCount(newCount);
    }
  };

  // 참가자 드로어 토글
  const toggleParticipantDrawer = () => {
    setIsParticipantDrawerOpen(!isParticipantDrawerOpen);
  };

  // 툴박스 토글
  const toggleToolbox = () => {
    setIsToolboxOpen(!isToolboxOpen);
  };
  
  // 손들기 토글
  const toggleRaiseHand = () => {
    setIsRaiseHand(!isRaiseHand);
    // 손들기 이벤트를 서버에 전송할 수 있음
    if (socketRef.current) {
      console.log(`손들기 상태 변경: ${!isRaiseHand}`);
      socketRef.current.emit('raise hand', { raised: !isRaiseHand, userId: socketRef.current.id });
    }
  };

  // 미디어 선택 모달
  const renderMediaSelector = () => {
    if (!showMediaSelector) return null;
    
    return (
      <MediaSelectorOverlay className="meeting-entry">
        <MediaSelectorCard className="meeting-entry-card">
          <LanguageSelect />
          <MediaSelectorTitle>{t('참여 방법을 선택하세요')}</MediaSelectorTitle>
          <MediaOptionContainer className="meeting-media-options">
            <MediaOptionButton 
              onClick={() => selectMediaType('camera')}
              color={colors.primary}
            >
              <IconContainer color="79, 70, 229">
                <VideoIcon style={{ width: "2.5rem", height: "2.5rem", color: colors.primary }} />
              </IconContainer>
              <OptionLabel>{t('화상 카메라')}</OptionLabel>
              <OptionDescription>{t('카메라와 마이크 사용')}</OptionDescription>
            </MediaOptionButton>
            
            {canShareScreen ? <MediaOptionButton
              onClick={() => selectMediaType('screen')}
              color={colors.secondary}
            >
              <IconContainer color="14, 165, 233">
                <Share2Icon style={{ width: "2.5rem", height: "2.5rem", color: colors.secondary }} />
              </IconContainer>
              <OptionLabel>{t('화면 공유')}</OptionLabel>
              <OptionDescription>{t('화면과 오디오 공유')}</OptionDescription>
            </MediaOptionButton> : <MediaOptionButton onClick={() => selectMediaType('audio')} color={colors.secondary}>
              <IconContainer color="14, 165, 233"><MicIcon style={{ width: '2.5rem', height: '2.5rem' }} /></IconContainer>
              <OptionLabel>{t('음성으로 참여')}</OptionLabel>
              <OptionDescription>{t('마이크만 사용')}</OptionDescription>
            </MediaOptionButton>}
          </MediaOptionContainer>
          {!canShareScreen && <p style={{ color: colors.textLight, fontSize: '0.875rem', lineHeight: 1.6, marginTop: '1rem' }}>
            {t('이 기기에서는 다른 참여자의 공유 화면을 볼 수 있습니다. 내 화면 공유는 PC에서 시작하세요.')}
          </p>}
        </MediaSelectorCard>
      </MediaSelectorOverlay>
    );
  };
  // 참가자 비디오 렌더링
  const renderParticipantVideo = (participant, isSmall = false) => {
    return (
      <VideoContainer className="meeting-tile">
        {!participant.stream || participant.isVideoOff ? (
          <VideoPlaceholder className="meeting-placeholder">
            {/* <Avatar small={isSmall}>
              {participant.name ? (
                <AvatarImage src={`https://ui-avatars.com/api/?name=${participant.name}&background=random`} alt={participant.name} />
              ) : (
                <div>{participant.peerID?.substring(0, 1) || "U"}</div>
              )}
            </Avatar> */}
          </VideoPlaceholder>
        ) : (
          <VideoFrame>
            <video
              ref={video => {
                if (video && participant.stream) {
                  video.srcObject = participant.stream;
                }
              }}
              autoPlay
              playsInline
              muted={participant.isMuted}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <AudioLevel style={{ transform: `scaleX(${participant.audioLevel || 0})` }} />
          </VideoFrame>
        )}

        <VideoInfo>
          <VideoInfoContent>
            <VideoInfoLeft>
              {participant.isSpeaking && (
                <Icon>
                  <Volume2Icon />
                </Icon>
              )}
              <VideoName small={isSmall} inVideo={true}>
                {participant.name || t('사용자 {id}').replace('{id}', () => participant.peerID?.substring(0, 4))}
              </VideoName>
            </VideoInfoLeft>
            <VideoInfoRight>
              {participant.isMuted && (
                <Icon>
                  <MicOffIcon />
                </Icon>
              )}
              {participant.isVideoOff && (
                <Icon>
                  <VideoOffIcon />
                </Icon>
              )}
            </VideoInfoRight>
          </VideoInfoContent>
        </VideoInfo>
      </VideoContainer>
    );
  };

  // 참가자 드로어 렌더링
  const renderParticipantDrawer = () => {
    const participantsList = [
      // 로컬 사용자
      {
        id: 'local',
        name: entry?.name || t('나 (호스트)'),
        avatar: `https://ui-avatars.com/api/?name=You&background=random`,
        isSpeaking: false,
        isHost: true,
        isMuted: isMuted,
        isVideoOff: isVideoOff,
        isScreenSharing: isScreenSharing,
        audioLevel: 0,
      },
      // 원격 참가자들 (피어)
      ...peers.map(peer => ({
        id: peer.peerID,
        name: (entry && peer.name) || t('사용자 {id}').replace('{id}', () => peer.peerID.substring(0, 4)),
        avatar: `https://ui-avatars.com/api/?name=${peer.peerID.substring(0, 2)}&background=random`,
        isSpeaking: false,
        isHost: false,
        isMuted: false, // 실제 상태는 피어 연결에서 가져와야 함
        isVideoOff: false, // 실제 상태는 피어 연결에서 가져와야 함
        isScreenSharing: false,
        audioLevel: 0,
      }))
    ];

    return (
      <ParticipantDrawer className="meeting-drawer" isOpen={isParticipantDrawerOpen}>
        <DrawerHeader>
          <h3>{t('참여자 ({count})').replace('{count}', participantsList.length)}</h3>
          <Button variant="ghost" aria-label={t('참여자 목록 닫기')} title={t('참여자 목록 닫기')} onClick={toggleParticipantDrawer}>
            <XIcon />
          </Button>
        </DrawerHeader>
        {participantsList.map((participant) => (
          <ParticipantItem key={participant.id}>
            <Avatar small>
              {entry ? participant.name.slice(0, 2) : <AvatarImage src={participant.avatar} alt={participant.name} />}
            </Avatar>
            <ParticipantInfo>
              <VideoName inVideo={false}>{participant.name}</VideoName>
              {participant.isHost && <span style={{ fontSize: "0.75rem" }}>{t('호스트')}</span>}
            </ParticipantInfo>
            <ParticipantActions>
              {participant.isSpeaking && (
                <Icon>
                  <Volume2Icon />
                </Icon>
              )}
              {participant.isMuted && (
                <Icon>
                  <MicOffIcon />
                </Icon>
              )}
            </ParticipantActions>
          </ParticipantItem>
        ))}
      </ParticipantDrawer>
    );
  };

  // 레이아웃 렌더링
  const renderLayout = () => {
    // 피어와 로컬 사용자를 결합하여 참가자 목록 생성
    const allParticipants = [
      // 로컬 사용자
      {
        id: 'local',
        name: entry?.name || t('내 자신'),
        stream: localStreamRef.current,
        isSpeaking: false,
        isHost: true,
        isMuted: isMuted,
        isVideoOff: isVideoOff,
        isScreenSharing: isScreenSharing,
        audioLevel: 0,
      },
      // 원격 참가자 (피어)
      ...peers.map(peer => {
        const stream = peer.remoteStream || null;

        return {
          id: peer.peerID,
          name: (entry && peer.name) || t('사용자 {id}').replace('{id}', () => peer.peerID.substring(0, 4)),
          stream: stream,
          isSpeaking: false,
          isHost: false,
          isMuted: false,
          isVideoOff: !stream || !stream.getVideoTracks().length || !stream.getVideoTracks()[0].enabled,
          audioLevel: 0,
        };
      })
    ];
    
    // 선택한 레이아웃 크기에 맞게 더미 참가자 추가
    const dummyParticipants = Array.from({ length: Math.max(0, displayedParticipantCount - allParticipants.length) }, (_, index) => ({
      id: `dummy-${index}`,
      name: t('빈 자리 {number}').replace('{number}', index + 1),
      stream: null,
      isSpeaking: false,
      isHost: false,
      isMuted: true,
      isVideoOff: true,
      audioLevel: 0,
    }));
    
    // 활성 참가자들 (최대 displayedParticipantCount만큼)
    const activeParticipants = entry ? allParticipants : [...allParticipants, ...dummyParticipants].slice(0, displayedParticipantCount);


    // 모바일: 세로 우선 배치
    if (isMobileView) {
      const cols = activeParticipants.length <= 2 ? 1 : 2;
      const rows = Math.ceil(activeParticipants.length / cols);
      return (
        <div
          style={{
            display: "grid",
            height: "100%",
            width: "100%",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            gap: "0.25rem",
          }}
        >
          {activeParticipants.map((participant) => (
            <div key={participant.id} style={{ width: "100%", height: "100%", minHeight: 0 }}>
              {renderParticipantVideo(participant, activeParticipants.length > 2)}
            </div>
          ))}
        </div>
      );
    }

    // 데스크톱 레이아웃
    switch (activeParticipants.length) {
      case 1:
        return (
          <div style={{ width: "100%", height: "100%" }}>
            {renderParticipantVideo(activeParticipants[0])}
          </div>
        );
      case 2:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr",
              gap: "0.55rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      case 3:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "3fr 1fr",
              gap: "0.55rem",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              {renderParticipantVideo(activeParticipants[0])}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateRows: "1fr 1fr",
                gap: "0.55rem",
                height: "100%",
              }}
            >
              {activeParticipants.slice(1, 3).map((participant) => (
                <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                  {renderParticipantVideo(participant, true)}
                </div>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: "0.55rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      case 5:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "3fr 2fr",
              gap: "0.55rem",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              {renderParticipantVideo(activeParticipants[0])}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                gap: "0.45rem",
                height: "100%",
              }}
            >
              {activeParticipants.slice(1, 5).map((participant) => (
                <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                  {renderParticipantVideo(participant, true)}
                </div>
              ))}
            </div>
          </div>
        );
      case 6:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: "0.55rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      case 7:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "2fr 1fr",
              gap: "0.55rem",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              {renderParticipantVideo(activeParticipants[0])}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr 1fr",
                gap: "0.55rem",
                height: "100%",
              }}
            >
              {activeParticipants.slice(1, 7).map((participant) => (
                <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                  {renderParticipantVideo(participant, true)}
                </div>
              ))}
            </div>
          </div>
        );
      case 8:
        return (
          <div
            style={{
              display: "grid",
              height: "100%",
              width: "100%",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: "0.45rem",
            }}
          >
            {activeParticipants.map((participant) => (
              <div key={participant.id} style={{ width: "100%", height: "100%" }}>
                {renderParticipantVideo(participant)}
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  // 모달이 열려있지 않으면 아무것도 렌더링하지 않음
  if (!isOpen) return null;

  // 회의 시간 포맷팅
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 연결 상태 표시
  const getConnectionStatusDisplay = () => {
    switch(connectionStatus) {
      case 'connected':
        return <span style={{color: colors.success}}>{t('연결됨')}</span>;
      case 'disconnected':
        return <span style={{color: colors.danger}}>{t('연결 끊김')}</span>;
      case 'error':
        return <span style={{color: 'orange'}}>{t('연결 오류')}</span>;
      default:
        return null;
    }
  };

  return (
    <ModalContainer>
      <FlexColumn>
        {/* 미디어 선택 모달 */}
        {renderMediaSelector()}
        
        {/* Header */}
        <Header className="meeting-header">
          <HeaderLeft>
            <Button className="meeting-close-duplicate" variant="ghost" aria-label={t('회의 닫기')} title={t('회의 닫기')} onClick={onClose}>
              <XIcon />
            </Button>
            <div>
            <Title>
                {t('화상 채팅 -')} <MeetingTimer seconds={meetingTime} /> <ConnectionStatus status={connectionStatus} />
              </Title>
            </div>
          </HeaderLeft>
          <HeaderRight className="meeting-header-right">
            <LanguageSelect />
            <Button 
              variant="ghost" 
              disabled={displayedParticipantCount <= 2} 
              onClick={decreaseParticipants}
              className="meeting-layout-step" aria-label={t('표시 인원 줄이기')}
              title={t('표시 인원 줄이기')}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "1rem",
                  width: "1rem",
                }}
              >
                -
              </span>
            </Button>
            <ParticipantCount>
              <UsersIcon />
              <ParticipantCountText>{entry ? t('[{count}명]').replace('{count}', peers.length + 1) : t('{count}인 / 8인').replace('{count}', displayedParticipantCount)}</ParticipantCountText>
            </ParticipantCount>
            <Button 
              variant="ghost" 
              disabled={displayedParticipantCount >= 8}
              onClick={increaseParticipants}
              className="meeting-layout-step" aria-label={t('표시 인원 늘리기')}
              title={t('표시 인원 늘리기')}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "1rem",
                  width: "1rem",
                }}
              >
                +
              </span>
            </Button>
            <span>{t('[{count}명]').replace('{count}', peers.length + 1)}</span>
            <Button
              variant="ghost"
              onClick={toggleParticipantDrawer}
              aria-label={t(isParticipantDrawerOpen ? '참여자 목록 닫기' : '참여자 목록 열기')}
              title={t(isParticipantDrawerOpen ? '참여자 목록 닫기' : '참여자 목록 열기')}
              aria-expanded={isParticipantDrawerOpen}
              style={{
                backgroundColor: isParticipantDrawerOpen ? "rgba(255, 255, 255, 0.1)" : "transparent",
              }}
            >
              <UsersIcon />
            </Button>
          </HeaderRight>
        </Header>

        {/* Main Content */}
        <MainContent>
          <VideoGrid className="meeting-grid">
            {renderLayout()}
          </VideoGrid>

          {/* Local Video (Hidden) */}
          <video 
            ref={userVideoRef} 
            autoPlay 
            playsInline 
            muted 
            style={{ display: 'none' }} 
          />

          {/* Toolbox */}
          {isToolboxOpen && (
            <ToolboxContainer>
              <ToolboxButton variant="ghost" onClick={toggleRaiseHand}>
                <HandIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <ChatIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <EmojiIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <SettingsIcon />
              </ToolboxButton>
              <ToolboxButton variant="ghost">
                <UserPlusIcon />
              </ToolboxButton>
            </ToolboxContainer>
          )}
        </MainContent>

        {/* Participant Drawer */}
        {renderParticipantDrawer()}

        {/* Controls */}
        <Controls className="meeting-controls">
          <Button className="meeting-leave" variant="destructive" aria-label={t('회의 나가기')} title={t('회의 나가기')} onClick={onClose}>
            <PhoneOffIcon />
          </Button>

          <Button variant={isMuted ? "destructive" : "secondary"} aria-label={t(isMuted ? '마이크 켜기' : '마이크 끄기')} title={t(isMuted ? '마이크 켜기' : '마이크 끄기')} aria-pressed={isMuted} onClick={toggleMute}>
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </Button>

          <Button variant={isVideoOff ? "destructive" : "secondary"} aria-label={t(isVideoOff ? '카메라 켜기' : '카메라 끄기')} title={t(isVideoOff ? '카메라 켜기' : '카메라 끄기')} aria-pressed={isVideoOff} onClick={toggleVideo}>
            {isVideoOff ? <VideoOffIcon /> : <VideoIcon />}
          </Button>

          {canShareScreen && <Button
            variant={isScreenSharing ? "destructive" : "secondary"}
            onClick={toggleScreenSharing}
            aria-label={t(isScreenSharing ? '화면 공유 중지' : '화면 공유')}
            title={t(isScreenSharing ? '화면 공유 중지' : '화면 공유')}
            aria-pressed={isScreenSharing}
          >
            <Share2Icon />
          </Button>}
        </Controls>
      </FlexColumn>
    </ModalContainer>
  );
};

export default VideoConferenceModal;
